export interface PriorityFactors {
  revenue: number;      // Expected revenue amount
  health: number;       // Health score (0-5)
  strategic: number;    // Strategic importance (0-5)
  token: number;        // Token potential (0-5)
}

export interface PriorityScores {
  totalScore: number;
  revenueScore: number;
  healthScore: number;
  strategicScore: number;
  tokenScore: number;
}

export class PriorityCalculator {
  private static readonly REVENUE_WEIGHT = 40;
  private static readonly HEALTH_WEIGHT = 30;
  private static readonly STRATEGIC_WEIGHT = 21;
  private static readonly TOKEN_WEIGHT = 10;

  /**
   * Calculate priority score based on the Excel formula
   * =IF(COUNT(B2:E2)<4,"",LET(revenueWeight,40,healthWeight,30,strategicWeight,21,tokenWeight,10,revenue,B2,token,C2,strategic,D2,health,E2,revenueScore,IFS(revenue>=100000,revenueWeight,revenue>=50000,revenueWeight*0.7,revenue>=20000,revenueWeight*0.4,TRUE,0),tokenScore,(token/5)*tokenWeight,strategicScore,(strategic/5)*strategicWeight,healthScore,(health/5)*healthWeight,totalScore,revenueScore+tokenScore+strategicScore+healthScore,totalScore))
   */
  static calculatePriority(factors: PriorityFactors): PriorityScores | null {
    // Check if we have all 4 factors (equivalent to COUNT(B2:E2)<4)
    if (!this.hasAllFactors(factors)) {
      return null;
    }

    const { revenue, health, strategic, token } = factors;

    // Calculate revenue score based on thresholds
    const revenueScore = this.calculateRevenueScore(revenue);

    // Calculate other scores (scale 0-5 to percentage)
    const tokenScore = (token / 5) * this.TOKEN_WEIGHT;
    const strategicScore = (strategic / 5) * this.STRATEGIC_WEIGHT;
    const healthScore = (health / 5) * this.HEALTH_WEIGHT;

    // Calculate total score
    const totalScore = revenueScore + tokenScore + strategicScore + healthScore;

    return {
      totalScore,
      revenueScore,
      healthScore,
      strategicScore,
      tokenScore
    };
  }

  private static hasAllFactors(factors: PriorityFactors): boolean {
    return factors.revenue !== undefined && 
           factors.health !== undefined && 
           factors.strategic !== undefined && 
           factors.token !== undefined;
  }

  private static calculateRevenueScore(revenue: number): number {
    if (revenue >= 100000) {
      return this.REVENUE_WEIGHT; // 40 points
    } else if (revenue >= 50000) {
      return this.REVENUE_WEIGHT * 0.7; // 28 points
    } else if (revenue >= 20000) {
      return this.REVENUE_WEIGHT * 0.4; // 16 points
    } else {
      return 0; // 0 points
    }
  }

  /**
   * Get the maximum possible score
   */
  static getMaxScore(): number {
    return this.REVENUE_WEIGHT + this.HEALTH_WEIGHT + this.STRATEGIC_WEIGHT + this.TOKEN_WEIGHT;
  }

  /**
   * Get score percentage (0-100)
   */
  static getScorePercentage(scores: PriorityScores): number {
    return (scores.totalScore / this.getMaxScore()) * 100;
  }

  /**
   * Get priority level based on score
   */
  static getPriorityLevel(scores: PriorityScores): 'High' | 'Medium' | 'Low' {
    const percentage = this.getScorePercentage(scores);
    if (percentage >= 70) return 'High';
    if (percentage >= 40) return 'Medium';
    return 'Low';
  }

  /**
   * Get color for priority level
   */
  static getPriorityColor(level: 'High' | 'Medium' | 'Low'): string {
    switch (level) {
      case 'High': return 'bg-red-900 text-red-300';
      case 'Medium': return 'bg-yellow-900 text-yellow-300';
      case 'Low': return 'bg-green-900 text-green-300';
    }
  }
} 